import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { FaBrain, FaSyncAlt, FaPlay } from 'react-icons/fa';
import { getDeepSeekResponse } from '../../services/api/deepseek';

const mono = "'JetBrains Mono', 'Consolas', monospace";
const accent = '#00B4D8';
const borderSoft = 'rgba(255, 255, 255, 0.06)';

const CACHE_PREFIX = 'arcana_ai_comparative_v1';

/* ---------- styled ---------- */

const Panel = styled.section`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 1.25rem;
  margin-top: 1.25rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0 0 1rem;
  padding: 0 0 0.7rem;
  border-bottom: 1px solid ${borderSoft};
  flex-wrap: wrap;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.55rem;

  svg { color: ${accent}; }
`;

const ModelPill = styled.span`
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${accent};
  border: 1px solid rgba(0, 180, 216, 0.35);
  background: rgba(0, 180, 216, 0.08);
  padding: 2px 7px;
  border-radius: 3px;
`;

const Stamp = styled.span`
  margin-left: auto;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-disabled);
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.8rem;
  background: rgba(0, 180, 216, 0.08);
  color: ${accent};
  border: 1px solid rgba(0, 180, 216, 0.35);
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;

  svg { font-size: 0.75rem; }

  &:hover:not(:disabled) {
    background: rgba(0, 180, 216, 0.16);
    border-color: rgba(0, 180, 216, 0.55);
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: var(--text-disabled);
  font-size: 0.92rem;

  p { margin: 0 0 1rem; }
`;

const ErrorBox = styled.div`
  padding: 0.95rem 1rem;
  text-align: center;
  background: rgba(239, 71, 111, 0.06);
  border: 1px solid rgba(239, 71, 111, 0.3);
  color: var(--alert-error);
  border-radius: 6px;
  font-size: 0.88rem;
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1.5rem;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 180, 216, 0.25);
    border-top-color: ${accent};
    border-radius: 50%;
    animation: ${spin} 0.9s linear infinite;
  }
`;

const Output = styled.div`
  background: rgba(11, 19, 32, 0.55);
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 4px;
  padding: 1rem 1.15rem;
  color: var(--text-secondary);
  line-height: 1.55;
  font-size: 0.92rem;

  h1, h2, h3, h4 {
    color: var(--text-primary);
    margin: 1em 0 0.45em;
    font-weight: 600;
    line-height: 1.3;
  }
  h1 { font-size: 1.15em; }
  h2 { font-size: 1.05em; letter-spacing: 0.04em; }
  h3 { font-size: 0.95em; text-transform: uppercase; letter-spacing: 0.1em; color: ${accent}; }

  p { margin: 0 0 0.75em; }
  ul, ol { margin: 0 0 0.85em 1.2em; padding: 0; }
  li { margin-bottom: 0.3em; }
  strong { color: var(--text-primary); }
  em { color: var(--text-primary); font-style: italic; }
  blockquote {
    border-left: 2px solid ${accent};
    padding-left: 0.85em;
    margin: 0.6em 0;
    color: var(--text-disabled);
  }
  code {
    font-family: ${mono};
    font-size: 0.88em;
    padding: 1px 5px;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 3px;
    color: #cfe9f4;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.6em 0;
    font-size: 0.9em;
  }
  th, td {
    padding: 5px 9px;
    border: 1px solid ${borderSoft};
    text-align: left;
  }
  th {
    background: rgba(255, 255, 255, 0.04);
    font-family: ${mono};
    font-size: 0.7em;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const Footer = styled.div`
  margin-top: 0.8rem;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
`;

/* ---------- helpers ---------- */

function competitorLite(c) {
  return {
    nombre: c.name,
    partido: c.party,
    lideres: c.leaders,
    estrategia: c.strategy,
    fortalezas: c.strengths,
    debilidades: c.weaknesses,
    temas: c.main_topics || c.mainTopics,
  };
}

function buildPrompt({ competitors, myCandidateName }) {
  const lite = (competitors || []).map(competitorLite);
  return `Eres un analista político senior. A continuación tenés la matriz comparativa de los competidores en una elección local en San Nicolás de los Arroyos. El candidato propio es **${myCandidateName}**.

Datos en JSON:
${JSON.stringify(lite, null, 2)}

Generá un **informe ejecutivo** en español, en formato markdown, con la siguiente estructura exacta:

## Síntesis
2-3 oraciones que resuman el escenario actual de la contienda.

## Posicionamiento por competidor
Para cada competidor: una sola oración con su núcleo estratégico y su mayor vulnerabilidad.

## Brechas y solapamientos
Bullet points indicando temas en disputa, brechas que el candidato propio (${myCandidateName}) puede explotar y solapamientos con otros competidores.

## Recomendaciones tácticas
Lista numerada (3 a 5 ítems) de movimientos concretos para ${myCandidateName} en las próximas 4 semanas. Cada ítem: acción + justificación basada en la matriz.

## Riesgos a vigilar
Bullet points con 2-3 riesgos que pueden cambiar el escenario.

Sé conciso, evitá repetir literalmente la matriz, usá negritas con moderación. No incluyas título principal ni saludos.`;
}

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function fmtTs(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d)) return null;
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ---------- componente ---------- */

const AIComparativeReport = ({ competitors = [], myCandidateName = 'Federico Chouhy (LLA)' }) => {
  const cacheKey = useMemo(() => {
    const slugs = (competitors || [])
      .map((c) => c.slug || c.name)
      .sort()
      .join('|');
    return `${CACHE_PREFIX}:${slugs}`;
  }, [competitors]);

  const [report, setReport] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Hidratar desde cache cuando cambia el set de competidores
  useEffect(() => {
    const cached = readCache(cacheKey);
    if (cached) {
      setReport(cached.report || null);
      setGeneratedAt(cached.generatedAt || null);
    } else {
      setReport(null);
      setGeneratedAt(null);
    }
    setError(null);
  }, [cacheKey]);

  const generate = async () => {
    if (!competitors || competitors.length === 0) {
      setError('No hay competidores cargados para analizar.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const prompt = buildPrompt({ competitors, myCandidateName });
      const text = await getDeepSeekResponse({
        prompt,
        max_tokens: 1200,
        temperature: 0.5,
      });
      const ts = new Date().toISOString();
      setReport(text);
      setGeneratedAt(ts);
      writeCache(cacheKey, { report: text, generatedAt: ts });
    } catch (err) {
      console.error('AIComparativeReport error', err);
      setError(err?.message || 'No se pudo generar el informe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Panel>
      <Header>
        <Title>
          <FaBrain /> Análisis IA · Matriz comparativa
        </Title>
        <ModelPill>IA</ModelPill>
        {generatedAt && <Stamp>Generado · {fmtTs(generatedAt)}</Stamp>}
        <ActionBtn type="button" onClick={generate} disabled={loading} style={{ marginLeft: generatedAt ? 0 : 'auto' }}>
          {report ? <FaSyncAlt /> : <FaPlay />}
          {loading ? 'Generando…' : report ? 'Regenerar' : 'Generar análisis'}
        </ActionBtn>
      </Header>

      {error && <ErrorBox>{error}</ErrorBox>}

      {loading && <Loading>Procesando matriz</Loading>}

      {!loading && !error && !report && (
        <Empty>
          <p>Generá un informe estratégico a partir de los competidores actuales.</p>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-disabled)' }}>
            La IA cruza fortalezas, debilidades y temas para devolver síntesis,
            brechas y recomendaciones tácticas.
          </p>
        </Empty>
      )}

      {!loading && report && (
        <>
          <Output>
            <ReactMarkdown>{report}</ReactMarkdown>
          </Output>
          <Footer>
            Cacheado localmente · Regenerá cuando cambien las propuestas o el escenario
          </Footer>
        </>
      )}
    </Panel>
  );
};

export default AIComparativeReport;
